import type { GridGeometry } from '../../grid';
import type { MapDefinition } from '../../model/gamestate/GameState';
import type { Viewport } from './viewport';

export interface WorldRect {
    left: number;
    top: number;
    right: number;
    bottom: number;
    width: number;
    height: number;
}

export interface ChunkTileBounds {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
}

export interface BoardChunk {
    key: string;
    chunkX: number;
    chunkY: number;
    tileBounds: ChunkTileBounds;
    contentWorldBounds: WorldRect;
    worldBounds: WorldRect;
}

export interface TileDrawMetrics {
    originX: number;
    originY: number;
    centroidX: number;
    centroidY: number;
    centerX: number;
    centerY: number;
    spanWidth: number;
    spanHeight: number;
}

export interface ChunkTileSizeOptions {
    minTilesPerChunk?: number;
    maxTilesPerChunk?: number;
    targetChunkPixelSize?: number;
    preferredChunkSizes?: readonly number[];
}

export function resolveTileDrawMetrics(
    grid: GridGeometry,
    tileSize: number,
    tileX: number,
    tileY: number
): TileDrawMetrics {
    const origin = grid.tileToPixel(tileX, tileY, tileSize);
    const nextColumnOrigin = grid.tileToPixel(tileX + 1, tileY, tileSize);
    const nextRowOrigin = grid.tileToPixel(tileX, tileY + 1, tileSize);
    const centroid = grid.tileCentroid(tileSize, tileX, tileY);
    const spanWidth = Math.max(tileSize, nextColumnOrigin.x - origin.x);
    const spanHeight = Math.max(tileSize, nextRowOrigin.y - origin.y);
    return {
        originX: origin.x,
        originY: origin.y,
        centroidX: centroid.x,
        centroidY: centroid.y,
        centerX: origin.x + centroid.x,
        centerY: origin.y + centroid.y,
        spanWidth,
        spanHeight,
    };
}

export function resolveChunkTileSize(tileSize: number, options: ChunkTileSizeOptions = {}): number {
    const {
        minTilesPerChunk = 4,
        maxTilesPerChunk = 16,
        targetChunkPixelSize = 768,
        preferredChunkSizes = [16, 12, 9, 6, 4],
    } = options;
    if (!Number.isFinite(tileSize) || tileSize <= 0) {
        return maxTilesPerChunk;
    }
    const desiredTilesPerChunk = targetChunkPixelSize / tileSize;
    const filteredPreferredChunkSizes = preferredChunkSizes.filter(
        (size) => size >= minTilesPerChunk && size <= maxTilesPerChunk,
    );
    if (filteredPreferredChunkSizes.length === 0) {
        return Math.max(
            minTilesPerChunk,
            Math.min(maxTilesPerChunk, Math.round(desiredTilesPerChunk)),
        );
    }

    let bestChunkSize = filteredPreferredChunkSizes[0];
    let bestDistance = Math.abs(desiredTilesPerChunk - bestChunkSize);
    for (let index = 1; index < filteredPreferredChunkSizes.length; index += 1) {
        const candidate = filteredPreferredChunkSizes[index];
        const distance = Math.abs(desiredTilesPerChunk - candidate);
        if (distance < bestDistance) {
            bestChunkSize = candidate;
            bestDistance = distance;
        }
    }

    return bestChunkSize;
}

function rectFromEdges(left: number, top: number, right: number, bottom: number): WorldRect {
    return {
        left,
        top,
        right,
        bottom,
        width: Math.max(0, right - left),
        height: Math.max(0, bottom - top),
    };
}

function rectsIntersect(a: WorldRect, b: WorldRect): boolean {
    return !(a.right < b.left || a.bottom < b.top || a.left > b.right || a.top > b.bottom);
}

function expandRect(rect: WorldRect, amount: number): WorldRect {
    return rectFromEdges(
        rect.left - amount,
        rect.top - amount,
        rect.right + amount,
        rect.bottom + amount,
    );
}

export class RenderBoard {
    private readonly map: MapDefinition;
    private readonly grid: GridGeometry;
    private readonly tileSize: number;
    private readonly chunkTileSize: number;
    private readonly chunkCols: number;
    private readonly chunkRows: number;
    private readonly chunkCache = new Map<string, BoardChunk>();
    private worldBoundsCache: WorldRect | null = null;

    constructor(map: MapDefinition, grid: GridGeometry, tileSize: number, chunkTileSize: number) {
        this.map = map;
        this.grid = grid;
        this.tileSize = tileSize;
        this.chunkTileSize = chunkTileSize;
        this.chunkCols = Math.max(1, Math.ceil(this.map.width / this.chunkTileSize));
        this.chunkRows = Math.max(1, Math.ceil(this.map.height / this.chunkTileSize));
    }

    public getChunkCount(): { cols: number; rows: number } {
        return { cols: this.chunkCols, rows: this.chunkRows };
    }

    public getChunkTileSize(): number {
        return this.chunkTileSize;
    }

    public getChunkKey(chunkX: number, chunkY: number): string {
        return `${chunkX}:${chunkY}`;
    }

    public getChunkTileBounds(chunkX: number, chunkY: number, gutterTiles = 0): ChunkTileBounds {
        void gutterTiles;
        const startX = Math.max(0, chunkX * this.chunkTileSize);
        const startY = Math.max(0, chunkY * this.chunkTileSize);
        const endX = Math.min(this.map.width, (chunkX + 1) * this.chunkTileSize);
        const endY = Math.min(this.map.height, (chunkY + 1) * this.chunkTileSize);
        return { startX, startY, endX, endY };
    }

    public getChunk(chunkX: number, chunkY: number, gutterTiles = 0): BoardChunk {
        const key = `${chunkX}:${chunkY}:${gutterTiles}`;
        const cached = this.chunkCache.get(key);
        if (cached) {
            return cached;
        }

        const tileBounds = this.getChunkTileBounds(chunkX, chunkY, gutterTiles);
        const contentWorldBounds = this.getTileRangeWorldBounds(tileBounds);
        const worldBounds = gutterTiles > 0
            ? expandRect(contentWorldBounds, gutterTiles * this.tileSize)
            : contentWorldBounds;
        const chunk = {
            key: this.getChunkKey(chunkX, chunkY),
            chunkX,
            chunkY,
            tileBounds,
            contentWorldBounds,
            worldBounds,
        };
        this.chunkCache.set(key, chunk);
        return chunk;
    }

    public getVisibleChunks(viewport: Viewport, preloadChunks = 0, gutterTiles = 0): BoardChunk[] {
        const visibleWorldRect = viewport.getVisibleWorldRect();
        const chunks: BoardChunk[] = [];
        const seen = new Set<string>();
        for (let chunkY = 0; chunkY < this.chunkRows; chunkY += 1) {
            for (let chunkX = 0; chunkX < this.chunkCols; chunkX += 1) {
                const chunk = this.getChunk(chunkX, chunkY, gutterTiles);
                if (!rectsIntersect(chunk.worldBounds, visibleWorldRect)) {
                    continue;
                }
                if (!seen.has(chunk.key)) {
                    seen.add(chunk.key);
                    chunks.push(chunk);
                }
                for (let y = Math.max(0, chunkY - preloadChunks); y <= Math.min(this.chunkRows - 1, chunkY + preloadChunks); y += 1) {
                    for (let x = Math.max(0, chunkX - preloadChunks); x <= Math.min(this.chunkCols - 1, chunkX + preloadChunks); x += 1) {
                        const preloadChunk = this.getChunk(x, y, gutterTiles);
                        if (!seen.has(preloadChunk.key)) {
                            seen.add(preloadChunk.key);
                            chunks.push(preloadChunk);
                        }
                    }
                }
            }
        }
        return chunks;
    }

    public getTileWorldCenter(tileX: number, tileY: number): { x: number; y: number } {
        const metrics = this.getTileDrawMetrics(tileX, tileY);
        return {
            x: metrics.centerX,
            y: metrics.centerY,
        };
    }

    public getTileDrawMetrics(tileX: number, tileY: number): TileDrawMetrics {
        return resolveTileDrawMetrics(this.grid, this.tileSize, tileX, tileY);
    }

    public getWorldBounds(): WorldRect {
        if (this.worldBoundsCache) {
            return this.worldBoundsCache;
        }
        this.worldBoundsCache = this.getTileRangeWorldBounds({
            startX: 0,
            startY: 0,
            endX: this.map.width,
            endY: this.map.height,
        });
        return this.worldBoundsCache;
    }

    private getTileRangeWorldBounds(bounds: ChunkTileBounds): WorldRect {
        if (bounds.startX >= bounds.endX || bounds.startY >= bounds.endY) {
            return rectFromEdges(0, 0, 0, 0);
        }

        let left = Number.POSITIVE_INFINITY;
        let top = Number.POSITIVE_INFINITY;
        let right = Number.NEGATIVE_INFINITY;
        let bottom = Number.NEGATIVE_INFINITY;

        for (let tileY = bounds.startY; tileY < bounds.endY; tileY += 1) {
            for (let tileX = bounds.startX; tileX < bounds.endX; tileX += 1) {
                const center = this.getTileWorldCenter(tileX, tileY);
                left = Math.min(left, center.x - this.tileSize);
                top = Math.min(top, center.y - this.tileSize);
                right = Math.max(right, center.x + this.tileSize);
                bottom = Math.max(bottom, center.y + this.tileSize);
            }
        }

        return rectFromEdges(left, top, right, bottom);
    }
}
